import { useEffect, useState, memo } from "react";
import ReactECharts from "echarts-for-react";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { meteringService } from "service/meteringService";
import localStorageService from "service/localStorageService";
import ChartBox from "./ChartBox";
import useChartWidth from "hooks/useChartWidth";
import { isHttpSuccess } from "utils/functions";
import ChartActionDropdown from "components/dashboard/ChartActionDropdown";
import { ALL_MONTHS_LIST } from "constant";

interface MonthlyDataState {
    emailData: number[];
    smsData: number[];
    notificationData: number[];
    dashboardData: number[];
    offloadData: number[];
    eventsEnabledData: number[];
    eventsTriggeredData: number[];
}

interface MonthlyDataBody {
    [key: string]: number[];
}

interface TokenLookup {
    [key: string]: string;
    emailData: string;
    smsData: string;
    notificationData: string;
    dashboardData: string;
    offloadData: string;
    eventsEnabledData: string;
    eventsTriggeredData: string;
}

const EventCounterChart = (props: any) => {
    const {
        panel: { uuid: panelId, name },
        selectedDashboardUUID,
        handleOnChangeDashboardUUID,
        showLabels = ["ENABLED_EVENT", "Event Counter"],
        displayConfig = {},
        isVisible,
        visibleRef,
        zoomLevel,
    } = props;
    const {
        title = `Count of events`,
        subTitle = `Events enabled X Events triggered`,
        chartColor = [],
        getChartSeries,
    } = displayConfig;

    const [isLoading, setIsLoading] = useState(true);
    const { boxWidth } = useChartWidth({
        ref: visibleRef,
        occupiedWidth: 110,
    });

    const orgId: string = localStorageService.getItem("orgId");

    const [monthlyData, setMonthlyData] = useState<MonthlyDataState>({
        emailData: [],
        smsData: [],
        notificationData: [],
        dashboardData: [],
        offloadData: [],
        eventsEnabledData: [],
        eventsTriggeredData: [],
    });

    const [yearDropdown, setYearDropdown] = useState<number[]>([]);
    const [displayedYear, setDisplayedYear] = useState<number>(moment().year());

    useEffect(() => {
        const selected: any = {
            year: displayedYear,
        };
        const getDataCount = async () => {
            const res: any = await meteringService.getMeteringInfo({
                year: selected.year,
                organization_id: orgId,
            });

            if (isHttpSuccess(res.status)) {
                const ownerOrg: string = Object.keys(res.data).filter(
                    (k: string) => k !== "start_from"
                )[0];

                const {
                    start_from: {
                        year: yearOfSubscription = moment().year(),
                    } = {},
                } = res.data;

                const monthlyDataBody: MonthlyDataBody = {
                    emailData: [],
                    smsData: [],
                    notificationData: [],
                    dashboardData: [],
                    offloadData: [],
                    eventsEnabledData: [],
                    eventsTriggeredData: [],
                };

                const tokenLookup: TokenLookup = {
                    emailData: "email",
                    smsData: "sms",
                    notificationData: "notification",
                    dashboardData: "dashboard",
                    offloadData: "data offload",
                    eventsEnabledData: "activated event",
                    eventsTriggeredData: "triggered event",
                };

                for (let m in res.data[ownerOrg]) {
                    for (let t in tokenLookup) {
                        monthlyDataBody[t].length === 0
                            ? monthlyDataBody[t].push(
                                  res.data[ownerOrg][m][tokenLookup[t]] || 0
                              )
                            : (monthlyDataBody[t][Number(m) - 1] =
                                  (monthlyDataBody[t][Number(m) - 1] || 0) +
                                  (res.data[ownerOrg][m][tokenLookup[t]] || 0));
                    }
                }

                setMonthlyData((prevState) => ({
                    ...prevState,
                    emailData: monthlyDataBody.emailData,
                    smsData: monthlyDataBody.smsData,
                    notificationData: monthlyDataBody.notificationData,
                    dashboardData: monthlyDataBody.dashboardData,
                    offloadData: monthlyDataBody.offloadData,
                    eventsEnabledData: monthlyDataBody.eventsEnabledData,
                    eventsTriggeredData: monthlyDataBody.eventsTriggeredData,
                }));

                let yearOptions: number[] = [];

                for (let y = yearOfSubscription; y <= moment().year(); y++) {
                    yearOptions.push(y);
                }

                setYearDropdown(yearOptions);
            }
            setIsLoading(false);
        };
        if (isVisible) {
            getDataCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedYear, isVisible]);

    const option = {
        backgroundColor: "#2f3a51",
        responsive: true,
        maintainAspectRatio: false,
        color: chartColor,
        legend: {
            data: showLabels,
            bottom: 15,
            left: 20,
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontSize: 14,
                fontWeight: 400,
            },
            padding: 0,
            itemGap: 30,
            itemWidth: 10,
            itemHeight: 10,
        },

        tooltip: {},
        title: {
            show: true,
            left: 14,
            top: 15,
            text: name || title,
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 18,
                overflow: "truncate",
                ellipsis: "...",
                width: boxWidth,
            },
            subtext: subTitle,
            subtextStyle: {
                color: "#828FAB",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 14,
                overflow: "truncate",
                ellipsis: "...",
            },
        },
        toolbox: {
            feature: {
                saveAsImage: {
                    icon: "path://M8.79999 3.71991C7.92975 3.71991 7.07906 3.97797 6.35548 4.46144C5.63191 4.94492 5.06795 5.63211 4.73493 6.4361C4.4019 7.2401 4.31477 8.12479 4.48454 8.97831C4.65432 9.83182 5.07337 10.6158 5.68872 11.2312C6.30407 11.8465 7.08808 12.2656 7.94159 12.4354C8.79511 12.6051 9.6798 12.518 10.4838 12.185C11.2878 11.852 11.975 11.288 12.4584 10.5644C12.9419 9.84084 13.2 8.99015 13.2 8.11991C13.2 6.95296 12.7364 5.8338 11.9113 5.00864C11.0861 4.18348 9.96694 3.71991 8.79999 3.71991ZM8.79999 11.6399C8.1038 11.6399 7.42324 11.4335 6.84438 11.0467C6.26552 10.6599 5.81436 10.1101 5.54794 9.46695C5.28152 8.82376 5.21181 8.116 5.34763 7.43319C5.48345 6.75038 5.8187 6.12317 6.31098 5.63089C6.80326 5.13861 7.43046 4.80336 8.11327 4.66754C8.79608 4.53172 9.50384 4.60143 10.147 4.86785C10.7902 5.13427 11.34 5.58544 11.7268 6.1643C12.1135 6.74316 12.32 7.42372 12.32 8.11991C12.32 9.05347 11.9491 9.9488 11.289 10.6089C10.6289 11.2691 9.73355 11.6399 8.79999 11.6399Z M14.52 1.95998H12.5928L12.2276 1.22958C12.0602 0.930245 11.8192 0.678578 11.5274 0.498406C11.2356 0.318234 10.9026 0.215518 10.56 0.199982H7.04C6.69739 0.215518 6.36442 0.318234 6.0726 0.498406C5.78079 0.678578 5.53978 0.930245 5.3724 1.22958L5.0072 1.95998H3.08C2.26313 1.95998 1.47972 2.28448 0.902111 2.86209C0.324499 3.4397 0 4.22311 0 5.03998V12.08C0 12.8968 0.324499 13.6803 0.902111 14.2579C1.47972 14.8355 2.26313 15.16 3.08 15.16H14.52C15.3369 15.16 16.1203 14.8355 16.6979 14.2579C17.2755 13.6803 17.6 12.8968 17.6 12.08V5.03998C17.6 4.22311 17.2755 3.4397 16.6979 2.86209C16.1203 2.28448 15.3369 1.95998 14.52 1.95998ZM16.72 12.08C16.72 12.6635 16.4882 13.223 16.0756 13.6356C15.663 14.0482 15.1035 14.28 14.52 14.28H3.08C2.49652 14.28 1.93694 14.0482 1.52436 13.6356C1.11178 13.223 0.88 12.6635 0.88 12.08V5.03998C0.88 4.4565 1.11178 3.89693 1.52436 3.48435C1.93694 3.07177 2.49652 2.83998 3.08 2.83998H5.28C5.36125 2.83973 5.44085 2.81699 5.50997 2.77428C5.57909 2.73156 5.63503 2.67054 5.6716 2.59798L6.16 1.62558C6.25235 1.47089 6.38047 1.34061 6.53359 1.24568C6.68671 1.15074 6.86037 1.09392 7.04 1.07998H10.56C10.7396 1.09392 10.9133 1.15074 11.0664 1.24568C11.2195 1.34061 11.3476 1.47089 11.44 1.62558L11.9284 2.59798C11.965 2.67054 12.0209 2.73156 12.09 2.77428C12.1591 2.81699 12.2387 2.83973 12.32 2.83998H14.52C15.1035 2.83998 15.663 3.07177 16.0756 3.48435C16.4882 3.89693 16.72 4.4565 16.72 5.03998V12.08Z",
                },
            },
            top: 12,
            right: 50,
            emphasis: {
                iconStyle: {
                    textPosition: "top",
                    textPadding: -4,
                },
            },
            iconStyle: {
                borderColor: "#00CBAF",
            },
        },
        xAxis: {
            data: ALL_MONTHS_LIST,
            boundaryGap: true,
            axisLine: {
                lineStyle: {
                    color: "#48597D",
                },
            },
        },
        yAxis: {
            splitLine: {
                show: true,
                lineStyle: {
                    color: "#4B5569",
                },
            },
        },
        grid: {
            bottom: 50,
            left: 30,
            right: 30,
            top: 90,
            containLabel: true,
        },

        series: getChartSeries(monthlyData),
    };

    const renderYear = () =>
        yearDropdown.map((year: number) => {
            return (
                <Dropdown.Item
                    key={year}
                    onClick={() => {
                        setDisplayedYear(year);
                    }}
                >
                    {year}
                </Dropdown.Item>
            );
        });
    return (
        <ChartBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget">
                <ChartActionDropdown
                    panelId={panelId}
                    selectedDashboardUUID={selectedDashboardUUID}
                    onDelete={handleOnChangeDashboardUUID}
                />
                <Dropdown alignRight>
                    <Dropdown.Toggle variant="" className="last-data">
                        {displayedYear}
                    </Dropdown.Toggle>
                    <Dropdown.Menu key="years">{renderYear()}</Dropdown.Menu>
                </Dropdown>
                <ContentWrapper isLoading={isLoading}>
                    <ReactECharts
                        className="chart-box"
                        option={option}
                        theme={"dark"}
                    />
                </ContentWrapper>
            </div>
        </ChartBox>
    );
};

export default memo(EventCounterChart);
